import { useState } from 'react';
import { volumes } from '@/mocks/comic';

export default function VolumeDemo() {
  const [activeVolumeId, setActiveVolumeId] = useState(volumes[0].id);
  const [reading, setReading] = useState<{ chapterId: number; page: number } | null>(null);

  const activeVolume = volumes.find((v) => v.id === activeVolumeId) ?? volumes[0];
  const currentChapter = reading ? activeVolume.chapters.find((c) => c.id === reading.chapterId) ?? null : null;

  const openChapter = (chapterId: number) => {
    setReading({ chapterId, page: 1 });
  };

  const goPrev = () => {
    if (!reading) return;
    const idx = activeVolume.chapters.findIndex((c) => c.id === reading.chapterId);
    if (idx > 0) setReading({ chapterId: activeVolume.chapters[idx - 1].id, page: 1 });
  };

  const goNext = () => {
    if (!reading) return;
    const idx = activeVolume.chapters.findIndex((c) => c.id === reading.chapterId);
    if (idx < activeVolume.chapters.length - 1) {
      setReading({ chapterId: activeVolume.chapters[idx + 1].id, page: 1 });
    }
  };

  return (
    <section id="volumes" className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 md:px-6 lg:grid-cols-2">
        <div>
          <span className="inline-block rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
            分卷阅读
          </span>
          <h2 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            长篇漫画，分卷轻松管理
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-600">
            漫蛙漫画把超长连载按故事线拆分为清晰的分卷结构，每卷内再分章节。
            跨卷跳转一目了然，阅读进度按卷保存，追几百话的大长篇也不再混乱。
          </p>

          <ul className="mt-6 space-y-3">
            {[
              '按卷折叠目录，查找范围瞬间缩小',
              '跨卷跳转、卷内直达双向定位',
              '每一卷进度独立保存、随心跳跃',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-check-line text-xs" aria-hidden="true"></i>
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-sm">
          <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
              <i className="ri-book-open-line text-primary-600" aria-hidden="true"></i>
              《星海远征记》
            </div>
            {reading ? (
              <button
                type="button"
                onClick={() => setReading(null)}
                className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground-500 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line" aria-hidden="true"></i> 返回目录
              </button>
            ) : (
              <span className="text-xs text-foreground-400">目录跳转演示</span>
            )}
          </div>

          {reading && currentChapter ? (
            <div className="p-6">
              <div className="rounded-xl bg-background-100 p-5">
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
                    {activeVolume.name}
                  </span>
                  <span className="text-xs text-foreground-500">
                    第 {reading.page} / {currentChapter.pages} 页
                  </span>
                </div>
                <h4 className="mt-4 font-heading text-xl font-bold text-foreground-950">
                  {currentChapter.title}
                </h4>
                <div className="mt-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={goPrev}
                    disabled={activeVolume.chapters.findIndex((c) => c.id === reading.chapterId) === 0}
                    className="flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:opacity-40"
                  >
                    <i className="ri-arrow-left-s-line" aria-hidden="true"></i> 上一话
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={
                      activeVolume.chapters.findIndex((c) => c.id === reading.chapterId) ===
                      activeVolume.chapters.length - 1
                    }
                    className="flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-40"
                  >
                    下一话 <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                  </button>
                </div>
                <p className="mt-4 text-xs leading-relaxed text-foreground-500">
                  演示：在这里选择目录中的章节即可直达对应内容，进度会自动记录。
                </p>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex gap-2 border-b border-background-200 px-4 pt-3">
                {volumes.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => {
                      setActiveVolumeId(v.id);
                      setReading(null);
                    }}
                    className={`whitespace-nowrap rounded-t-lg px-4 py-2 text-sm font-medium transition-colors ${
                      v.id === activeVolumeId
                        ? 'border-b-2 border-primary-500 text-primary-700'
                        : 'text-foreground-500 hover:text-foreground-800'
                    }`}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
              <div className="p-3">
                <p className="px-2 pb-2 text-xs text-foreground-500">{activeVolume.desc}</p>
                <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {activeVolume.chapters.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => openChapter(c.id)}
                        className="flex w-full items-center justify-between rounded-lg border border-background-200 px-3 py-2.5 text-left text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50"
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-medium">{c.title}</span>
                          {c.isNew && (
                            <span className="rounded-full bg-secondary-500 px-2 py-0.5 text-[10px] font-semibold text-background-50">
                              最新
                            </span>
                          )}
                        </span>
                        <i className="ri-arrow-right-up-line text-foreground-300" aria-hidden="true"></i>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}